import { signal } from '@preact/signals';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { N, OFFER, STEPS, WALK, type Flash, type StepContext, type StepId, type Task } from '../data/guide';
import {
  calendarFilmOpen, storyOpen, infoMinimised, introOpen, loadJSON, panel, pointingPaused, pointingPrompt, reliving, saveJSON, selection,
  showToast, timeLive, viewMode, onceOfferShown, whatToShow, type Selection } from '../state';
import { services } from '../app';
import { enuToAltAz, refractEnu } from '../lib/math';
import { Icon } from './icons';
import { holeAround, placeCard, type Box, type Insets } from './spotlight';

// How to use Ahillah, in the app itself: "Show me around" lights up the real controls one at a time, "Show me"
// (from Help's "How do I…?") opens the right place and lights up the right switch, and a new user is offered the
// tour once. Every word is in src/data/guide.ts.

// ---------- The offer to a new user: once ----------

type OfferState = 'none' | 'pending' | 'shown' | 'done';
const storedOffer = () => loadJSON<{ offer?: string }>('guide', {}).offer;
/** 'pending' from the end of the welcome until a quiet moment on the sky; 'shown' while it waits for an answer. */
export const guideOffer = signal<OfferState>(storedOffer() === 'pending' ? 'pending' : 'none');
function setOffer(s: OfferState) {
  guideOffer.value = s;
  // Shown counts as done: if the page reloads before an answer, it is not offered again.
  saveJSON('guide', { offer: s === 'pending' ? 'pending' : 'done' });
}
/** The welcome is over: offer the tour at the first quiet moment, unless it has been offered before. */
export function offerAfterWelcome() {
  if (guideOffer.peek() === 'none' && storedOffer() !== 'done') setOffer('pending');
}
/** The welcome's "Show me around first": the tour straight away, and no offer later. */
export function tourFromWelcome() {
  setOffer('done');
  showAround();
}
/** Help has been opened: its first card offers the tour, so the offer on the sky is not needed. */
export function helpSeen() {
  if (guideOffer.peek() === 'pending' || guideOffer.peek() === 'shown') setOffer('done');
}

// ---------- Show me around ----------

const walkStart = signal(0);
/** Start the tour, at its first step or at any other. */
export function showAround(step: StepId | number = 0) {
  const i = typeof step === 'number' ? step : STEPS.findIndex((s) => s.id === step);
  walkStart.value = Math.max(0, Math.min(STEPS.length - 1, i));
  panel.value = 'walkthrough';
}

const q = (sel: string) => document.querySelector<HTMLElement>(sel);
/** The element, if it takes up room on the screen. */
const onScreen = (el: HTMLElement | null) => (el && el.getBoundingClientRect().width > 0 ? el : null);
const dockButton = (label: string) => [...document.querySelectorAll<HTMLElement>('.dock .dock-btn')].find((b) => b.textContent?.trim() === label) ?? null;
const DOCK_STEPS = new Set<StepId>(['point', 'search', 'tonight', 'layers', 'more']);

/** The control a step lights up (the sky is a region, worked out with the card). */
function controlFor(id: StepId): HTMLElement | null {
  switch (id) {
    case 'point': return q('.dock .dock-main');
    case 'search': return dockButton(N.search);
    case 'tonight': return dockButton(N.tonight);
    case 'layers': return dockButton(N.layers);
    case 'more': return dockButton(N.more);
    case 'place': return q('.topbar > .chip:first-child');
    case 'time': return q('.topbar .chip-time, .topbar .chip-split');
    case 'settings': return q('.topbar .chip-right');
    // On a narrow phone showing another time the question mark gives way: Help is in Settings then.
    case 'help': return onScreen(q('.topbar .chip-help')) ?? q('.topbar .chip-right');
    default: return null;
  }
}

/** The screen's safe-area insets (notch, home bar), in pixels. */
function safeInsets(): Insets {
  const p = document.createElement('div');
  p.style.cssText = 'position:fixed;inset:0;visibility:hidden;pointer-events:none;padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px)';
  document.body.appendChild(p);
  const s = getComputedStyle(p);
  const v = { top: parseFloat(s.paddingTop) || 0, right: parseFloat(s.paddingRight) || 0, bottom: parseFloat(s.paddingBottom) || 0, left: parseFloat(s.paddingLeft) || 0 };
  p.remove();
  return v;
}

const box = (r: DOMRect): Box => ({ x: r.x, y: r.y, w: r.width, h: r.height });

/** The tour: the screen dims, one control at a time shows through a gold ring, and a card beside it says what it does. */
export function Walkthrough() {
  const [i, setI] = useState(() => walkStart.peek());
  const [said, setSaid] = useState('');
  // Turning the phone can hide or show the Help button, and the Help step's words depend on it.
  const [, redraw] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const card = useRef<HTMLDivElement>(null);
  const spot = useRef<HTMLDivElement>(null);
  const nextBtn = useRef<HTMLButtonElement>(null);
  const n = STEPS.length;
  const step = STEPS[i];
  const last = i === n - 1;
  const ctx: StepContext = {
    touch: matchMedia('(pointer: coarse)').matches,
    pointing: viewMode.value === 'ar',
    live: timeLive.value,
    helpButton: !!onScreen(q('.topbar .chip-help')),
  };
  const title = step.title(ctx);
  const text = step.text(ctx);
  const end = () => (panel.value = null);
  const go = (d: number) => setI((k) => Math.max(0, Math.min(n - 1, k + d)));

  // Taking the tour answers the offer. Focus comes to the card, so a screen reader reads the first step; at the
  // end it goes to the Help button, where the tour lives.
  useEffect(() => {
    helpSeen();
    const before = document.activeElement as HTMLElement | null;
    card.current?.focus({ preventScroll: true });
    return () => {
      walkStart.value = 0;
      setTimeout(() => {
        if (panel.peek() !== null) return;
        const to = onScreen(q('.topbar .chip-help')) ?? (before && document.contains(before) ? before : null);
        to?.focus({ preventScroll: true });
      }, 0);
    };
  }, []);

  // Each new step is read out; the focus stays on the card's buttons (Back is disabled on the first step).
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setSaid(`${WALK.progress(i + 1, n)}. ${title}. ${text}`);
    const a = document.activeElement as HTMLButtonElement | null;
    if (!card.current?.contains(a) || a?.disabled) nextBtn.current?.focus({ preventScroll: true });
  }, [i]);

  // Keys: Escape leaves, the arrows step, and Tab stays within the card.
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        end();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        go(e.key === 'ArrowRight' ? 1 : -1);
      } else if (e.key === 'Tab') {
        const els = [...(card.current?.querySelectorAll<HTMLElement>('button:not([disabled])') ?? [])];
        if (!els.length) return;
        const a = document.activeElement as HTMLElement | null;
        const inside = !!a && !!card.current?.contains(a);
        if (e.shiftKey && (!inside || a === els[0] || a === card.current)) {
          e.preventDefault();
          els[els.length - 1].focus();
        } else if (!e.shiftKey && (!inside || a === els[els.length - 1])) {
          e.preventDefault();
          els[0].focus();
        }
      }
    };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
  }, []);

  // Follow the control every frame (the dock rises in, the time pill changes width, the phone turns on its side)
  // and move the ring and the card only when something has changed. The first placement happens before the first
  // paint, so the card never shows anywhere else.
  useLayoutEffect(() => {
    let raf = 0;
    let lastKey = '';
    let safe = safeInsets();
    const onResize = () => {
      safe = safeInsets();
      redraw((x) => x + 1);
    };
    addEventListener('resize', onResize);
    const frame = () => {
      raf = requestAnimationFrame(frame);
      const c = card.current, s = spot.current, r = root.current;
      if (!c || !s || !r) return;
      const view = { w: innerWidth, h: innerHeight };
      const size = { w: c.offsetWidth, h: c.scrollHeight };
      let hole: (Box & { radius: number }) | null = null;
      let place;
      if (step.id === 'sky') {
        // The card sits above the dock; the ring takes in the sky between the top bar and the card.
        const dock = onScreen(q('.dock'));
        const anchor = dock ? box(dock.getBoundingClientRect()) : { x: 0, y: view.h - safe.bottom - 72, w: view.w, h: 62 };
        place = placeCard(anchor, size, view, safe);
        const bar = [...document.querySelectorAll<HTMLElement>('.topbar > *')].reduce((b, el) => Math.max(b, el.getBoundingClientRect().bottom), safe.top);
        const top = bar + 12;
        let bottom = (place.above ? place.top : anchor.y) - 12;
        if (bottom - top < 120) {
          // A phone on its side leaves little sky above the card: the ring takes in all of it, with the card inside.
          place = placeCard(anchor, size, view, safe, 24);
          bottom = anchor.y - 12;
        }
        if (bottom - top > 40) hole = { x: safe.left + 12, y: top, w: view.w - safe.left - safe.right - 24, h: bottom - top, radius: 24 };
      } else {
        const el = onScreen(controlFor(step.id));
        hole = el ? holeAround(box(el.getBoundingClientRect()), DOCK_STEPS.has(step.id) ? 4 : 6) : null;
        place = placeCard(hole, size, view, safe);
      }
      const key = [view.w, view.h, size.w, size.h, hole ? [hole.x, hole.y, hole.w, hole.h].map(Math.round).join() : '-', place.left, place.top, place.maxH].join('|');
      if (key === lastKey) return;
      lastKey = key;
      s.classList.toggle('wt-spot-none', !hole);
      if (hole) Object.assign(s.style, { left: `${hole.x}px`, top: `${hole.y}px`, width: `${hole.w}px`, height: `${hole.h}px`, borderRadius: `${hole.radius}px` });
      Object.assign(c.style, { left: `${place.left}px`, top: `${place.top}px`, maxHeight: `${place.maxH}px` });
      // Placed without moving the first time; after that the ring and the card glide.
      if (!r.classList.contains('wt-ready')) requestAnimationFrame(() => r.classList.add('wt-ready'));
    };
    frame();
    return () => {
      cancelAnimationFrame(raf);
      removeEventListener('resize', onResize);
    };
  }, [i]);

  return (
    <div class="wt" ref={root}>
      {/* Taps anywhere but the card do nothing: the tour shows the controls, it does not press them. */}
      <div class="wt-catch" aria-hidden="true" />
      <div class="wt-spot" ref={spot} aria-hidden="true" />
      <div class="wt-card" ref={card} role="dialog" aria-modal="true" aria-label={WALK.label} aria-describedby="wt-title wt-text" tabIndex={-1}>
        <div class="wt-top">
          <span class="wt-step tnum">{WALK.progress(i + 1, n)}</span>
          {!last && <button class="wt-skip" onClick={end}>{WALK.skip}</button>}
        </div>
        <h2 id="wt-title" key={`t${i}`}>{title}</h2>
        <p id="wt-text" key={`p${i}`}>{text}</p>
        <div class="tour-dots" aria-hidden="true">{STEPS.map((s, k) => <span key={s.id} class={k === i ? 'on' : ''} />)}</div>
        <div class="sr-only" aria-live="polite">{said}</div>
        {/* Last, and pinned to the card's foot: with very large text on a phone on its side the words scroll, not these. */}
        <div class="wt-controls">
          <button class="btn-ghost wt-back" disabled={i === 0} onClick={() => go(-1)}>{WALK.back}</button>
          <button class="btn small wt-next" ref={nextBtn} onClick={() => (last ? end() : go(1))}>{last ? WALK.done : WALK.next}</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Show me: from an answer in Help to the right place ----------

const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
let flashOff: ReturnType<typeof setTimeout> | undefined;

/** Do what a task's "Show me" says: a step of the tour, a sheet with its switch lit up, or a card. */
export function showMe(t: Task) {
  const s = t.show;
  if ('step' in s) return showAround(s.step);
  if ('card' in s) {
    const sel = upToShow(s.card);
    const key = JSON.stringify(sel);
    panel.value = null;
    infoMinimised.value = false;
    selection.value = sel;
    if (s.flash) pointOut(s.flash, () => panel.peek() === null && JSON.stringify(selection.peek()) === key);
  } else {
    const to = s.panel;
    panel.value = to;
    if (s.flash) pointOut(s.flash, () => panel.peek() === to);
  }
}

/**
 * The card to open: the body named if it is up, or else the first bright planet that is, or else the brightest named
 * star that is (the card turns the view to its object, and one below the horizon would leave only ground on show).
 */
function upToShow(id: string): Selection {
  const s = services.peek();
  const up = (sel: Selection) => {
    const loc = s?.engine.locate(sel);
    return !!loc && enuToAltAz(refractEnu(loc.enu)).alt > 10;
  };
  for (const b of [id, 'Jupiter', 'Venus', 'Saturn', 'Mars']) if (up({ kind: 'body', id: b })) return { kind: 'body', id: b };
  if (s) {
    const cat = s.engine.stars;
    const named = [...cat.info].filter(([, info]) => info.name).map(([i]) => i).sort((a, b) => cat.mag[a] - cat.mag[b]);
    for (const i of named.slice(0, 40)) if (up({ kind: 'star', index: i })) return { kind: 'star', index: i };
  }
  return { kind: 'body', id };
}

/** The latest Show me: one still looking when another starts gives up. */
let pointing = 0;
/**
 * Once the sheet or card has risen, light up the part named, bring it into view and give it the focus. `still` says
 * whether the place it was meant for is still open: if the person has moved on, nothing else is lit up.
 */
export function pointOut(f: Flash, still: () => boolean = () => true) {
  const me = ++pointing;
  const t0 = performance.now();
  const look = () => {
    if (me !== pointing || !still()) return;
    const el = findPart(f);
    if (el) return light(el);
    // Some parts appear a moment later (a card's Listen waits for its recordings): look again for a few seconds.
    if (performance.now() - t0 < 3000) setTimeout(look, 150);
  };
  setTimeout(look, 460);
}

function findPart(f: Flash): HTMLElement | null {
  for (const root of document.querySelectorAll<HTMLElement>('.sheet:not(.sheet-closing), .info-peek')) {
    if (f.sel) {
      const el = root.querySelector<HTMLElement>(f.sel);
      if (el) return el;
      continue;
    }
    const walk = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    for (let t = walk.nextNode(); t; t = walk.nextNode()) {
      if (t.nodeValue?.trim() !== f.text) continue;
      const el = f.row ? t.parentElement?.closest<HTMLElement>(f.row) : t.parentElement;
      if (el && root.contains(el)) return el;
    }
  }
  return null;
}

function light(el: HTMLElement) {
  const body = el.closest<HTMLElement>('.sheet-body');
  if (body) {
    const r = el.getBoundingClientRect(), b = body.getBoundingClientRect();
    // Only when it is out of sight (or under the pinned title): then into the middle of the sheet.
    if (r.top < b.top + 64 || r.bottom > b.bottom - 16) {
      body.scrollTo({ top: Math.max(0, body.scrollTop + (r.top - b.top) - (b.height - r.height) / 2), behavior: reduced() ? 'auto' : 'smooth' });
    }
  }
  document.querySelectorAll('[data-guide-flash]').forEach((x) => x.removeAttribute('data-guide-flash'));
  el.setAttribute('data-guide-flash', '');
  clearTimeout(flashOff);
  flashOff = setTimeout(() => el.removeAttribute('data-guide-flash'), 2600);
  const target = el.matches('button, input, summary, a') ? el : el.querySelector<HTMLElement>('input, button, summary, a');
  target?.focus({ preventScroll: true });
}

// ---------- The offer: once, on the sky, after a new user's first look round ----------

/**
 * Offered after the welcome, at the first quiet moment once the person has had a look: after the first card they
 * open has closed, or after 25 seconds. Quiet means nothing else on the screen asks for attention (no sheet, card,
 * film, note or prompt) and the phone is not being followed (someone holding it up to the sky is busy). It is
 * offered once: answered, ignored until the page closes, or the tour taken some other way, it never comes back.
 */
export function GuideOffer({ blocked }: { blocked: boolean }) {
  const st = guideOffer.value;
  const [armed, setArmed] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const focused = useRef(false);
  // The look round starts once nothing else is due (an update's film or note comes first, never back to back).
  useEffect(() => {
    if (st !== 'pending' || armed || blocked) return;
    const id = setTimeout(() => setArmed(true), 25000);
    const off = selection.subscribe((s) => void (s && setArmed(true)));
    return () => {
      clearTimeout(id);
      off();
    };
  }, [st, armed, blocked]);
  const idle = !blocked && panel.value === null && !selection.value && viewMode.value === 'manual' && !pointingPrompt.value && !pointingPaused.value
    && !reliving.value && !introOpen.value && !calendarFilmOpen.value && !storyOpen.value && !whatToShow.value && !onceOfferShown.value;
  useEffect(() => {
    if (st !== 'pending' || !armed || !idle) return;
    const id = setTimeout(() => setOffer('shown'), 1500);
    return () => clearTimeout(id);
  }, [st, armed, idle]);
  const visible = st === 'shown' && idle;
  const later = () => {
    setOffer('done');
    const help = onScreen(q('.topbar .chip-help'));
    showToast(help ? OFFER.later : OFFER.laterInSettings, 5000);
    setTimeout(() => (help ?? q('.topbar .chip-right'))?.focus({ preventScroll: true }), 0);
  };
  const yes = () => {
    setOffer('done');
    showAround();
  };
  // Focus comes to the card the first time it shows, so a screen reader reads it; Escape is Not now.
  useEffect(() => {
    if (!visible) return;
    if (!focused.current) {
      focused.current = true;
      ref.current?.focus({ preventScroll: true });
    }
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      later();
    };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
  }, [visible]);
  if (!visible) return null;
  return (
    <div class="guide-offer" role="dialog" aria-labelledby="guide-offer-title" aria-describedby="guide-offer-text" tabIndex={-1} ref={ref}>
      <h2 id="guide-offer-title"><span class="guide-offer-icon" aria-hidden="true"><Icon name="help" size={18} stroke={1.9} /></span>{OFFER.title}</h2>
      <p id="guide-offer-text">{OFFER.text}</p>
      <div class="guide-offer-actions">
        <button class="btn-ghost" onClick={later}>{OFFER.no}</button>
        <button class="btn" onClick={yes}>{OFFER.yes}</button>
      </div>
    </div>
  );
}
